import { useMemo, useState } from "react";
import api, { errMsg } from "../lib/api";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { toast } from "sonner";
import { Users, Mail } from "lucide-react";

const ROLE_ALIASES = {
  admin: "admin", administrador: "admin",
  buyer: "buyer", comprador: "buyer", compras: "buyer",
  approver: "approver", aprobador: "approver",
  warehouse: "warehouse", almacen: "warehouse", almacén: "warehouse", almacenista: "warehouse",
  maintenance: "maintenance", mantenimiento: "maintenance", mecanico: "maintenance", mecánico: "maintenance",
  viewer: "viewer", visor: "viewer", lectura: "viewer",
};

function parseRows(text) {
  return text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((line, i) => {
    const parts = line.split(/[,;\t]/).map((p) => p.trim());
    const [name = "", email = "", position = "", roleRaw = "viewer"] = parts;
    const role = ROLE_ALIASES[roleRaw.toLowerCase()] || "";
    const errors = [];
    if (!name) errors.push("nombre");
    if (!email.includes("@")) errors.push("correo");
    if (!role) errors.push(`rol "${roleRaw}"`);
    return { n: i + 1, name, email: email.toLowerCase(), position, role, errors };
  });
}

/** Admin: paste "Nombre, correo, puesto, rol" lines → creates users in bulk with a shared initial password. */
export function BulkUsersDialog({ t, onDone }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [password, setPassword] = useState("");
  const [sendWelcome, setSendWelcome] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const rows = useMemo(() => parseRows(text), [text]);
  const valid = rows.filter((r) => r.errors.length === 0);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/users/bulk", {
        send_welcome: sendWelcome,
        users: valid.map((r) => ({ name: r.name, email: r.email, position: r.position, role: r.role, password })),
      });
      setResult(data);
      toast.success(`${data.created} ${t("set.bulkCreated")}${data.errors ? ` · ${data.errors} ${t("set.bulkErrors")}` : ""}`);
      onDone?.();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setResult(null); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-none text-xs font-bold" data-testid="users-bulk-button">
          <Users size={13} className="mr-1" /> {t("set.bulkUsers")}
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="users-bulk-dialog">
        <DialogHeader>
          <DialogTitle className="uppercase font-black">{t("set.bulkUsers")}</DialogTitle>
          <DialogDescription className="text-xs">{t("set.bulkHint")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={7} data-testid="users-bulk-textarea"
            placeholder={"Juan Pérez, juan.perez@mafrissa.com, Jefe de Compras, comprador\nAna López, ana.lopez@mafrissa.com, Gerente de Planta, aprobador"}
            className="w-full border bg-background p-2 text-xs font-mono rounded-none focus:outline-none focus:ring-1 focus:ring-primary" />
          <div className="grid md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-[10px] font-bold uppercase tracking-widest">{t("set.bulkPassword")}</Label>
              <input type="text" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="users-bulk-password"
                className="w-full border bg-background h-8 px-2 text-xs rounded-none font-mono" placeholder="Mafrissa2026!" />
            </div>
            <label className="flex items-center gap-2 text-xs cursor-pointer mt-5" data-testid="users-bulk-welcome">
              <input type="checkbox" checked={sendWelcome} onChange={(e) => setSendWelcome(e.target.checked)} />
              <Mail size={12} /> <span className="font-bold">{t("set.bulkWelcome")}</span>
            </label>
          </div>
          {rows.length > 0 && !result && (
            <div className="tbl-wrap max-h-60 overflow-y-auto" data-testid="users-bulk-preview">
              <table className="tbl">
                <thead><tr><th>#</th><th>{t("set.name")}</th><th>Email</th><th>{t("set.position")}</th><th>{t("set.role")}</th><th></th></tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.n} className={r.errors.length ? "bg-destructive/10" : ""} data-testid={`users-bulk-row-${r.n}`}>
                      <td className="tabular-nums text-muted-foreground">{r.n}</td>
                      <td className="font-medium">{r.name}</td>
                      <td className="text-xs">{r.email}</td>
                      <td className="text-xs">{r.position || "—"}</td>
                      <td className="uppercase text-[10px] font-bold">{r.role ? t(`role.${r.role}`) : "?"}</td>
                      <td className="text-[10px] text-destructive">{r.errors.length ? `${t("set.bulkInvalid")}: ${r.errors.join(", ")}` : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {result && (
            <div className="tbl-wrap max-h-60 overflow-y-auto" data-testid="users-bulk-result">
              <table className="tbl">
                <thead><tr><th>Email</th><th>{t("common.status")}</th><th>{t("set.bulkWelcomeStatus")}</th></tr></thead>
                <tbody>
                  {result.results.map((r) => (
                    <tr key={r.email} className={r.status === "error" ? "bg-destructive/10" : ""}>
                      <td className="text-xs">{r.email}</td>
                      <td className="text-[10px] font-bold uppercase">{r.status === "created" ? t("common.created") : r.error}</td>
                      <td className="text-[10px]">{r.welcome_email || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!result && (
            <Button onClick={submit} disabled={busy || valid.length === 0 || password.length < 8}
              className="rounded-none font-bold" data-testid="users-bulk-submit">
              {busy ? "…" : `${t("common.create")} (${valid.length})`}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
